import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './RowNotices.css';

// Tap/click popover with the row's notice texts — the hover tooltip doesn't exist on touch screens.
// Rendered in a portal with fixed positioning so table cell overflow doesn't clip it.
function NoticePopover({ notices, anchor, onClose }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  useEffect(() => {
    const r = anchor.getBoundingClientRect();
    const w = ref.current.offsetWidth;
    setPos({ top: r.bottom + 4, left: Math.max(8, Math.min(r.left, window.innerWidth - w - 8)) });
  }, [anchor]);

  useEffect(() => {
    const onDown = (e) => { if (!ref.current?.contains(e.target) && !anchor.contains(e.target)) onClose(); };
    const onKey  = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onClose, true);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onClose, true);
      window.removeEventListener('resize', onClose);
    };
  }, [anchor, onClose]);

  return createPortal(
    <div ref={ref} className="rn-popover" style={pos || { visibility: 'hidden', top: 0, left: 0 }}
      onClick={e => e.stopPropagation()}>
      {notices.map((n, i) => (
        <div key={i} className="rn-popover-row">
          <span className={`rn-badge rn-${n.severity}`}>!</span>
          <span>{n.text}</span>
        </div>
      ))}
    </div>,
    document.body
  );
}

function RowNotices({ notices = [], editing, onChange }) {
  const [adding, setAdding] = useState(false);
  const [text, setText]     = useState('');
  const [sev, setSev]       = useState('yellow');
  const [popAnchor, setPopAnchor] = useState(null);

  const add = () => {
    if (!text.trim()) return;
    onChange([...notices, { text: text.trim(), severity: sev }]);
    setText(''); setSev('yellow'); setAdding(false);
  };

  const remove = (idx) => onChange(notices.filter((_, i) => i !== idx));

  return (
    <div className="rn-wrap">
      {notices.map((n, i) => (
        <span key={i}
          className={`rn-badge rn-${n.severity}`}
          title={n.text}
          onClick={(e) => {
            e.stopPropagation();
            if (editing) remove(i);
            else { const el = e.currentTarget; setPopAnchor(a => a === el ? null : el); }
          }}
          style={{ cursor: 'pointer' }}>!</span>
      ))}
      {popAnchor && !editing && (
        <NoticePopover notices={notices} anchor={popAnchor} onClose={() => setPopAnchor(null)} />
      )}
      {editing && !adding && (
        <button className="rn-add" onClick={e => { e.stopPropagation(); setAdding(true); }} title="Add notice">+</button>
      )}
      {editing && adding && (
        <div className="rn-form" onClick={e => e.stopPropagation()}>
          <span className={`rn-badge rn-${sev}`} style={{ cursor: 'pointer' }}
            onClick={() => setSev(s => s === 'yellow' ? 'red' : 'yellow')} title="Toggle severity">!</span>
          <input autoFocus value={text} onChange={e => setText(e.target.value)}
            placeholder="Notice…" className="rn-input"
            onKeyDown={e => {
              e.stopPropagation();
              if (e.key === 'Enter')  { add(); }
              if (e.key === 'Escape') { setAdding(false); setText(''); }
            }} />
          <button className="rn-ok"  onClick={add}>✓</button>
          <button className="rn-cancel" onClick={() => { setAdding(false); setText(''); }}>✕</button>
        </div>
      )}
    </div>
  );
}

export default RowNotices;
